import React, { useState, useEffect } from "react";

interface NavbarProps {
  setCurrentPage: React.Dispatch<React.SetStateAction<string>>;
  currentPage: string;
}

const Navbar: React.FC<NavbarProps> = ({ setCurrentPage, currentPage }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 900) {
        setMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Smooth scroll to section
  const handleScroll = (sectionId: string) => {
    // First ensure we're on home page where all sections exist
    setCurrentPage('home');
    
    // Small delay to allow page to render
    setTimeout(() => {
      const section = document.getElementById(sectionId);
      if (section) {
        window.scrollTo({
          top: section.offsetTop - 90,
          behavior: "smooth",
        });
      }
      closeMenu();
    }, 100);
  };

  return (
    <>
      <style>{`
        .navbar { 
          position: fixed;
          top: 0;
          width: 100%;
          background: rgba(255,255,255,0.9);
          backdrop-filter: blur(10px);
          padding: 0.8rem 2rem;
          z-index: 1000;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }

        .nav-container {
          max-width: 1300px;
          margin: auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .logo h2 {
          color: #f59e0b;
          font-weight: 600;
          margin: 0;
          cursor: pointer;
        }

        .logo span {
          color: #f59e0b;
        }

        .logo small {
          display: block;
          font-size: 0.7rem;
          color: #555;
        }

        .nav-links {
          display: flex;
          gap: 2rem;
          align-items: center;
        }

        .nav-links button {
          background: none;
          border: none;
          color: #333;
          font-size: 1rem;
          cursor: pointer;
          padding: 0.5rem 0;
          transition: color 0.3s;
        }

        .nav-links button:hover {
          color: #f59e0b;
        }

        .nav-links button.active {
          color: #f59e0b;
          font-weight: bold;
          border-bottom: 2px solid #f59e0b;
        }

        .quote-btn {
          background: #f59e0b;
          border: none;
          padding: 0.5rem 1.5rem;
          border-radius: 30px;
          color: white;
          cursor: pointer;
          font-weight: 500;
        }

        .hamburger {
          display: none;
          font-size: 1.8rem;
          cursor: pointer;
          color: #333;
        }

        .mobile-menu {
          display: none;
        }

        @media (max-width: 900px) {
          .nav-links {
            display: none;
          }

          .hamburger {
            display: block;
          }

          .mobile-menu {
            position: fixed;
            top: 70px;
            left: 0;
            width: 100%;
            background: white;
            display: none;
            flex-direction: column;
            gap: 1rem;
            padding: 2rem;
            box-shadow: 0 10px 20px rgba(0,0,0,0.1);
            z-index: 999;
          }

          .mobile-menu div {
            cursor: pointer;
            font-weight: 500;
            padding: 0.5rem;
            transition: color 0.3s;
          }

          .mobile-menu div:hover {
            color: #f59e0b;
            background: #fff5e6;
          }

          .mobile-menu.active {
            display: flex;
          }

          .mobile-quote {
            background: #f59e0b;
            padding: 0.7rem;
            border-radius: 25px;
            text-align: center;
            color: white;
            cursor: pointer;
          }
        }
      `}</style>

      <nav className="navbar">
        <div className="nav-container">
          <div className="logo" onClick={() => handleScroll("home")}>
            <h2>
              SAJ <span>ENTERPRISES</span>
            </h2>
            <small>SALES · SERVICE · SPARES</small>
          </div>

          <div className="nav-links">
            <button
              className={currentPage === 'home' ? 'active' : ''}
              onClick={() => handleScroll("home")}
            >
              Home
            </button>
            <button
              className={currentPage === 'about' ? 'active' : ''}
              onClick={() => handleScroll("about")}
            >
              About
            </button>
            <button
              className={currentPage === 'products' ? 'active' : ''}
              onClick={() => handleScroll("products")}
            >
              Products
            </button>
            <button
              className={currentPage === 'gallery' ? 'active' : ''}
              onClick={() => handleScroll("gallery")}
            >
              Gallery
            </button>
            <button
              className={currentPage === 'contact' ? 'active' : ''}
              onClick={() => handleScroll("contact")}
            >
              Contact
            </button>
            <button
              className="quote-btn"
              onClick={() => handleScroll("contact")}
            >
              Get Quote
            </button>
          </div>

          <div
            className={`hamburger ${menuOpen ? "active" : ""}`}
            onClick={toggleMenu}
          >
            ☰
          </div>
        </div>

        <div className={`mobile-menu ${menuOpen ? "active" : ""}`}>
          <div onClick={() => handleScroll("home")}>Home</div>
          <div onClick={() => handleScroll("about")}>About</div>
          <div onClick={() => handleScroll("products")}>Products</div>
          <div onClick={() => handleScroll("gallery")}>Gallery</div>
          <div onClick={() => handleScroll("contact")}>Contact</div>
          <div
            className="mobile-quote"
            onClick={() => handleScroll("contact")}
          >
            Get Quote
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;